"use client";

import { TooltipProvider } from "@/components/ui/tooltip";
import { FileExplorerContextProvider } from "@/providers/file-explorer-provider";
import { SidebarContextProvider } from "@/providers/sidebar-provider";
import { UploadModalProvider } from "@/providers/upload-modal-provider";
import { MyUserContextProvider } from "@/providers/user-provider";
import { QueryClientProvider } from "@tanstack/react-query";
import { getQueryClient } from "@/utils/get-query-client";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { FlashcardsProvider } from "@/features/flashcards/hooks/useFlashcards";
import WalkthroughProvider from "@/features/walkthroughs/components/WalkthroughProvider";
import { LanguageProvider } from "@/providers/language-provider";
import { PWAProvider, usePWA } from "@/providers/pwa-provider";
import { ClerkProvider } from "@clerk/nextjs";
import OrganizationAutoSelector from "@/components/shared/ui/OrganizationAutoSelector";
import { ContextProvider } from "@/providers/context-provider";
import { ReactNode } from "react";
import RememberOrg from "@/features/auth/components/RememberOrg";

if (typeof window !== "undefined") {
	posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
		api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST as string,
		ui_host: 'https://us.posthog.com',
		defaults: '2026-01-30',
		capture_pageview: false, // Disable automatic pageview capture, as we capture manually
		capture_pageleave: true,
		custom_campaign_params: ["ref"],
	});
}

export function Providers({ children }: { children: ReactNode }) {
	return (
		<PWAProvider>
			<ProvidersInner>{children}</ProvidersInner>
		</PWAProvider>
	);
}

function ProvidersInner({ children }: { children: ReactNode }) {
	const query_client = getQueryClient();
	const { is_pwa } = usePWA();

	return (
		<ClerkProvider
			standardBrowser={!is_pwa}
			appearance={{
				variables: {
					colorPrimary: "#2563eb", // Tailwind blue-600
					colorText: "#1e293b", // slate-800 for clean text
					colorBackground: "#f8fafc", // slate-50 for a clean background
					colorInputBackground: "#fff",
					colorInputText: "#1e293b",
					colorSuccess: "#22c55e", // green-500
					colorWarning: "#f59e42", // orange-400
					colorDanger: "#ef4444", // red-500
					colorTextOnPrimaryBackground: "#fff",
					colorTextSecondary: "#64748b", // slate-400
				}
			}}
		>
			<QueryClientProvider client={query_client}>
				<PostHogProvider client={posthog}>
					<MyUserContextProvider>
						<SidebarContextProvider>
							<FlashcardsProvider>
								<FileExplorerContextProvider>
									<UploadModalProvider>
										<WalkthroughProvider>
											<ContextProvider>
												<TooltipProvider delayDuration={400}>
													<LanguageProvider>
														<OrganizationAutoSelector />
														<RememberOrg />
														{children}
													</LanguageProvider>
												</TooltipProvider>
											</ContextProvider>
										</WalkthroughProvider>
									</UploadModalProvider>
								</FileExplorerContextProvider>
							</FlashcardsProvider>
						</SidebarContextProvider>
					</MyUserContextProvider>
				</PostHogProvider>
			</QueryClientProvider>
		</ClerkProvider>
	);
}
